<template>
  <profile-edit-section-card
    v-if="localState"
    title="Rate"
    breadcrumb-label="Profile"
    :saving="isSaving"
    :has-unsaved-changes="hasUnsavedChanges"
    @breadcrumb-click="cancelEdit"
    @save="handleSave"
    @cancel="cancelEdit"
  >
    <fieldset class="mb-24">
      <legend class="form__label pb-8">Services & Pricing</legend>
      <p class="text-caption mb-16">You can update your rates at anytime</p>
      <ul class="list-none">
        <li v-for="serviceRate in localState.petServicesOptions" :key="serviceRate.service">
          <PoppinsCheckbox
            v-model="serviceRate.selected"
            :checkbox-id="getCheckboxId(serviceRate.service)"
            :name="getCheckboxId(serviceRate.service)"
            :label-name="serviceRate.checkboxLabel"
            :aria-label="generateScreenReaderLabel(serviceRate.checkboxLabel)"
            @update:modelValue="serviceRate.selected = $event"
          />
          <PoppinsTextField
            v-if="serviceRate.selected"
            :id="textFieldId"
            :model-value="serviceRate.rate"
            :name="textFieldId"
            class="pb-24"
            :label-name="serviceRate.inputLabel"
            :aria-label="'Set your minimum rate'"
            type="text"
            pattern="[0-9]*"
            inputmode="numeric"
            :required="true"
            :status="serviceRate.status"
            :helper-text="serviceRate.status === HelperStatus.Error && serviceRate.helperText"
            :limit-input-to-pattern="true"
            @update:model-value="serviceRate.rate = $event"
            @blur="validate(serviceRate.service)"
          />
        </li>
      </ul>
    </fieldset>
    <poppins-dropdown
      id="distance-willing-to-travel"
      v-model="localState.distanceWillingToTravel"
      class="mb-16"
      label-name="How many miles from home are you willing to work?"
      :options="distanceOptions"
    />
    <poppins-dropdown
      id="max-number-of-pets"
      v-model="localState.maxNumberOfPets"
      label-name="How many pets you are comfortable caring for at a time?"
      :options="numberOfPetOptions"
    />
  </profile-edit-section-card>
</template>

<script lang="ts">
import PoppinsDropdown from '@/components/dropdown/Dropdown.vue';
import PoppinsCheckbox from '@/components/checkbox/Checkbox.vue';
import PoppinsTextField from '@/components/textfield/TextField.vue';
import ProfileEditSectionCard from '../../components/ProfileEditSectionCard.vue';
import profileEditMixin from '../../mixins/profileEditMixin';
import { distanceOptions, numberOfPetOptions } from '../../__types__/CareTypeFormOptions';
import { PetCareProfileFormState, petServicesOptions, serviceRateToServiceKey } from '../../__types__/PetCareProfileTypes.js';
import HelperStatus from '@/helpers/status.js';

export default {
  name: 'PetCareRate',
  components: {
    ProfileEditSectionCard,
    PoppinsDropdown,
    PoppinsCheckbox,
    PoppinsTextField,
  },
  mixins: [profileEditMixin],
  data(): any {
    return {
      localState: null,
      originalState: null,
      petServicesOptions,
      HelperStatus,
    };
  },
  computed: {
    distanceOptions: () => distanceOptions(),
    numberOfPetOptions: () => numberOfPetOptions(),
    hasUnsavedChanges(): boolean {
      if (!this.localState || !this.originalState) return false;
      return JSON.stringify(this.localState) !== JSON.stringify(this.originalState);
    },
  },
  watch: {
    $route() {
      this.initializeFormState();
    },
    formState: {
      handler(newState) {
        if (newState) {
          this.initializeFormState();
        }
      },
      immediate: true,
    },
  },
  mounted() {
    this.initializeFormState();
  },
  methods: {
    getCheckboxId(service): string {
      return `${service}-selected`;
    },
    generateScreenReaderLabel(label) {
      return `I offer ${label}`;
    },
    validate(service) {
      const serviceRate = this.localState.petServicesOptions.find((s) => s.service === service);
      if (!serviceRate) return;

      if (serviceRate.selected && (!serviceRate.rate || parseInt(serviceRate.rate) === 0)) {
        serviceRate.status = HelperStatus.Error;
      } else {
        serviceRate.status = HelperStatus.Neutral;
      }
    },
    initializeFormState() {
      const state = this.formState as PetCareProfileFormState | null;
      if (!state) return;

      this.localState = {
        distanceWillingToTravel: String(state.distanceWillingToTravel || 10),
        maxNumberOfPets: String(state.maxNumberOfPets || 1),
        petServicesOptions: Object.entries(this.petServicesOptions).map(([key, value]) => ({
          ...this.petServicesOptions[key],
          rate: state.serviceRates[value.service] / 100 || '',
          selected: !!state.serviceRates[value.service],
        })),
      };
      this.originalState = JSON.parse(JSON.stringify(this.localState));
    },
    async handleSave() {
      if (!this.localState) return;
      const invalidServices = this.localState.petServicesOptions.filter(
        (serviceRate) => serviceRate.selected && (!serviceRate.rate || parseInt(serviceRate.rate) === 0)
      );

      this.localState.petServicesOptions.forEach((serviceRate) => {
        serviceRate.status = invalidServices.includes(serviceRate) ? HelperStatus.Error : HelperStatus.Neutral;
      });

      if (invalidServices.length > 0) {
        return;
      }

      if (this.hasUnsavedChanges) {
        try {
          await this.saveSection({
            serviceRates: (() => {
              const serviceRates = [] as any;
              this.localState.petServicesOptions.forEach((serviceRate) => {
                if (serviceRate.selected && serviceRate.rate) {
                  serviceRates.push({
                    service: serviceRateToServiceKey[serviceRate.service],
                    rate: Number(serviceRate.rate) * 100,
                  });
                }
              });
              return serviceRates;
            })(),
            distanceWillingToTravel: Number(this.localState.distanceWillingToTravel),
            maxNumberOfPets: Number(this.localState.maxNumberOfPets),
          });
          // only re-initialize the form state if the save was successful, checkboxes do not work properly if the form state is not re-initialized
          this.initializeFormState();
        } catch {
          // this is only to prevent the form from resetting if the save fails, so we don't need to do anything here
        }
      }
    },
  },
};
</script>
